<script setup lang="ts">
/** GET /models/{id}/export · tabular export. Format and target, then the download. */
import Dialog from '~/components/ui/Dialog.vue'
import Choice from '~/components/ui/Choice.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'
import ParamRow from '~/components/inspector/ParamRow.vue'

const props = defineProps<{ entity: string; kind: 'model' | 'table'; facts: { label: string; value: string }[] }>()
defineEmits<{ close: []; submit: [payload: Record<string, unknown>] }>()
const format = ref(props.kind === 'model' ? 'adapter' : 'csv')
/* The file formats are named as the files themselves are named, because that is what the
   customer will find on disk; what each one is, and what it needs to run, is said in words
   beside it rather than left to be inferred from the format (W8-UI-07). */
const modelFormats = [
  { id: 'adapter', title: 'Only what training changed · safetensors', facts: 'the smallest download · runs on top of the base model, which you supply' },
  { id: 'gguf-q8_0', title: 'The whole model · GGUF Q8_0', facts: 'runs on its own in llama.cpp and Ollama · compressed · size measured after export' },
  { id: 'gguf-f16', title: 'The whole model · GGUF F16', facts: 'runs on its own · nothing dropped, so the largest file · size measured after export' },
]
const tableFormats = [
  { id: 'csv', title: 'CSV', facts: 'UTF-8 · header row' },
  { id: 'parquet', title: 'Parquet', facts: 'typed columns · smaller' },
  { id: 'jsonl', title: 'JSON lines', facts: 'one object per row' },
]
const formats = props.kind === 'model' ? modelFormats : tableFormats
</script>

<template>
  <Dialog :title="kind === 'model' ? 'Export model' : 'Export table'" :meta="entity" :width="480" @close="$emit('close')">
    <template #icon><Icon name="download" /></template>
    <div class="flex flex-col gap-1.5">
      <span class="text-xs text-muted-foreground">Format</span>
      <Choice v-for="o in formats" :key="o.id" :title="o.title" :facts="o.facts" :selected="format === o.id" @click="format = o.id" />
    </div>
    <div class="flex flex-col gap-[var(--measure-7px)] rounded-md border border-border p-2.5">
      <ParamRow v-for="x in facts" :key="x.label" :label="x.label" :value="x.value" />
    </div>
    <template #actions>
      <ActionButton label="Cancel" @click="$emit('close')" />
      <ActionButton label="Prepare download" variant="primary" @click="$emit('submit', { format })"><template #icon><Icon name="download" /></template></ActionButton>
    </template>
  </Dialog>
</template>
